import Barcode from "react-barcode";
import { Package } from "lucide-react";
import { API } from "@/lib/api";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const addrOf = (p, fallback) => (p && p.address ? `${p.address}, ${p.city}, ${p.state || ""} ${p.pincode}, ${p.country}` : fallback);
const nameOf = (p, fallback) => (p && p.entity_type === "company" ? (p.company_name || p.contact_person) : (p && p.full_name)) || fallback;

export function ProformaInvoice({ order }) {
  const s = order.sender || {};
  const r = order.receiver || {};
  const boxes = order.boxes || [];
  const total = order.declared_value != null ? order.declared_value : boxes.reduce((sum, box) => sum + (box.items || []).reduce((a, it) => a + (it.quantity || 1) * (it.value || 0), 0), 0);
  const aadhaar = /aadhaar|aadhar/i.test(s.kyc_type || "") ? s.kyc_number : "";
  const num = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const rows = boxes.flatMap((box, bi) => [
    <tr key={`h${bi}`} className="border-b border-slate-800"><td className="border-r border-slate-800" /><td className="p-1 font-bold text-center border-r border-slate-800" colSpan={4}>BOX-{bi + 1}</td></tr>,
    ...(box.items || []).map((it, ii) => (
      <tr key={`b${bi}i${ii}`} className="border-b border-slate-300">
        <td className="p-1 text-center border-r border-slate-800">{ii + 1}</td>
        <td className="p-1 text-center uppercase border-r border-slate-800">{it.name}</td>
        <td className="p-1 text-center border-r border-slate-800">{num(it.quantity)}</td>
        <td className="p-1 text-right border-r border-slate-800">{num(it.value)}</td>
        <td className="p-1 text-right">{num((it.quantity || 1) * (it.value || 0))}</td>
      </tr>
    )),
  ]);
  return (
    <div className="print-area bg-white text-slate-900 text-xs border border-slate-800" data-testid="proforma-invoice">
      <div className="text-center font-extrabold text-base py-1.5 border-b border-slate-800 tracking-wide">PROFORMA INVOICE</div>
      <div className="grid grid-cols-2">
        <div className="border-r border-slate-800">
          <div className="p-2 border-b border-slate-800">
            <div className="font-bold">CONSIGNOR</div>
            <div className="font-semibold">{nameOf(s, order.user_name)}</div>
            <div>{s.address}</div>
            <div>{s.city} {s.pincode}</div>
            <div>{[s.state, s.country || "India"].filter(Boolean).join(", ")}</div>
            {s.phone && <div>PH NO # {s.phone}</div>}
          </div>
          <div className="p-2">
            <div className="font-bold">CONSIGNEE / DECLARANT</div>
            <div className="font-semibold">{nameOf(r, order.recipient_name)}</div>
            {r.contact_person && <div>Attn: {r.contact_person}</div>}
            <div>{r.address}</div>
            <div>{r.city} {r.pincode}</div>
            <div>{[r.state, r.country].filter(Boolean).join(", ")}</div>
            {r.phone && <div>PH NO # {r.phone}</div>}
          </div>
        </div>
        <div>
          <div className="grid grid-cols-2 border-b border-slate-800">
            <div className="p-2 border-r border-slate-800">Invoice No: <span className="font-semibold">{order.commercial_invoice_number || order.invoice_number || "1"}</span><div>DATE: {fmtDate(order.created_at)}</div></div>
            <div className="p-2">EXP. REF: {order.tracking_number}</div>
          </div>
          {aadhaar && <div className="p-2 border-b border-slate-800 text-center">ADHAR NO # {aadhaar}</div>}
          <div className="p-2 border-b border-slate-800">
            <div className="font-bold text-center">WEIGHTS</div>
            {boxes.map((box, i) => <div key={i}>BOX-{i + 1} &nbsp; {box.weight} KGS</div>)}
            {boxes.length > 1 && <div className="font-semibold mt-0.5">TOTAL &nbsp; {order.weight} KGS</div>}
          </div>
          <div className="p-2 min-h-[2.5rem]">Buyer (if other than consignee)</div>
        </div>
      </div>
      <div className="grid grid-cols-2 border-t border-slate-800">
        <div className="border-r border-slate-800">
          <div className="grid grid-cols-2 text-center border-b border-slate-800 font-semibold">
            <div className="p-1 border-r border-slate-800">Country of Origin</div>
            <div className="p-1">Country of Final Destination</div>
          </div>
          <div className="grid grid-cols-2 text-center">
            <div className="p-1 border-r border-slate-800">{order.origin_country || "India"}</div>
            <div className="p-1">{order.destination_country}</div>
          </div>
        </div>
        <div className="p-2 flex items-center justify-center font-bold">FOR GIFT PURPOSE ONLY</div>
      </div>
      <table className="w-full border-t border-slate-800">
        <thead><tr className="border-b border-slate-800">
          <th className="p-1 border-r border-slate-800 w-10">S.NO</th>
          <th className="p-1 border-r border-slate-800 text-left">Description of Goods</th>
          <th className="p-1 border-r border-slate-800 w-20">Quantity</th>
          <th className="p-1 border-r border-slate-800 w-20">Rate</th>
          <th className="p-1 w-24">Value</th>
        </tr></thead>
        <tbody>
          {rows}
          <tr className="border-t border-slate-800 font-bold"><td className="border-r border-slate-800" colSpan={4} /><td className="p-1 text-right">{num(total)}</td></tr>
        </tbody>
      </table>
      <div className="grid grid-cols-2 border-t border-slate-800">
        <div className="p-2 text-center">We certify that the information given above is true and correct to the best of our knowledge</div>
        <div className="p-6 border-l border-slate-800 flex items-end justify-center">Signature &amp; Date</div>
      </div>
    </div>
  );
}

function PartyBlock({ title, party, name, phone, address, kycType, kycNo }) {
  const isCompany = party && party.entity_type === "company";
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{title}</div>
      <div className="text-sm font-semibold text-slate-900">{name}</div>
      {isCompany && party.contact_person && <div className="text-xs text-slate-600">Attn: {party.contact_person}</div>}
      <div className="text-xs text-slate-600 whitespace-pre-line">{address}</div>
      {phone && <div className="text-xs text-slate-600">Ph: {phone}</div>}
      {isCompany && party.gstin && <div className="text-xs text-slate-600">GST/Tax ID: {party.gstin}</div>}
      {isCompany && party.iec_code && <div className="text-xs text-slate-600">IEC: {party.iec_code}</div>}
      {isCompany && party.ad_code && <div className="text-xs text-slate-600">AD Code: {party.ad_code}</div>}
      {!isCompany && kycType && <div className="text-xs text-slate-600">{kycType}: {kycNo}</div>}
    </div>
  );
}

export function InvoiceView({ order }) {
  const b = order.branding || {};
  const s = order.sender || {};
  const r = order.receiver || {};
  const boxes = order.boxes || [];
  return (
    <div className="print-area bg-white text-slate-900 p-6 rounded-lg" data-testid="invoice-doc">
      <div className="flex items-start justify-between border-b-2 border-primary pb-4">
        <div className="flex items-center gap-2">
          {b.logo_file_id ? (
            <img src={`${API}/portal-logo/${b.logo_file_id}`} alt={b.company_name} className="h-11 w-11 rounded-lg object-contain border border-slate-200 bg-white" />
          ) : (
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-white"><Package className="h-6 w-6" /></div>
          )}
          <div>
            <div className="text-xl font-extrabold">{b.company_name || order.source_portal}</div>
            <div className="text-xs text-slate-600 whitespace-pre-line">{b.company_address}</div>
            {b.gstin && <div className="text-xs text-slate-600">GSTIN: {b.gstin}</div>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-extrabold uppercase tracking-wide text-primary">Invoice</div>
          <div className="text-xs text-slate-600">No: <span className="font-semibold">{order.invoice_number || "—"}</span></div>
          <div className="text-xs text-slate-600">Date: {fmtDate(order.created_at)}</div>
          <div className="text-xs text-slate-600">AWB: {order.tracking_number || "—"}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 py-4">
        <PartyBlock title="Bill From (Sender)" party={s} name={nameOf(s, order.user_name)} phone={s.phone || order.customer_phone}
          address={addrOf(s, order.pickup_address)} kycType={s.kyc_type} kycNo={s.kyc_number} />
        <PartyBlock title="Ship To (Receiver)" party={r} name={nameOf(r, order.recipient_name)} phone={r.phone || order.recipient_phone}
          address={addrOf(r, order.delivery_address)} kycType={r.kyc_type} kycNo={r.kyc_number} />
      </div>

      {(() => {
        const rows = [];
        boxes.forEach((box, bi) => (box.items || []).forEach((it) => rows.push({ ...it, box: bi + 1 })));
        const hasItems = rows.length > 0;
        return (
      <table className="w-full text-sm border-t border-slate-200">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
            <th className="py-2">#</th><th>Item</th><th className="text-center">Box</th><th className="text-center">Qty</th><th className="text-right">Unit Value</th><th className="text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {hasItems ? rows.map((it, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="py-2">{i + 1}</td><td>{it.name}</td>
              <td className="text-center">{it.box}</td><td className="text-center">{it.quantity}</td>
              <td className="text-right">{money(it.value)}</td>
              <td className="text-right">{money((it.quantity || 1) * (it.value || 0))}</td>
            </tr>
          )) : (boxes.length ? boxes.map((box, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="py-2">{i + 1}</td><td>{box.item_content}</td><td className="text-center">{i + 1}</td>
              <td className="text-center">—</td><td className="text-right">—</td><td className="text-right">—</td>
            </tr>
          )) : (
            <tr className="border-b border-slate-100"><td className="py-2">1</td><td>{order.package_type}</td><td className="text-center">1</td><td className="text-center">—</td><td className="text-right">—</td><td className="text-right">—</td></tr>
          ))}
        </tbody>
      </table>
        );
      })()}

      <div className="flex justify-between items-end pt-4">
        <div className="text-xs text-slate-600">
          <div>Service: <span className="font-semibold">{order.service_type}</span></div>
          <div>Route: {order.origin_country} → {order.destination_country}</div>
          <div>Boxes: {order.box_count || boxes.length || 1} · Total Weight: {order.weight} kg</div>
          {order.declared_value != null && order.declared_value > 0 && <div>Declared Value: {money(order.declared_value)}</div>}
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500 uppercase tracking-wide">Total Amount</div>
          <div className="text-2xl font-extrabold text-primary">{order.quoted_price != null ? money(order.quoted_price) : "—"}</div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-slate-200 text-[10px] text-slate-400 text-center">
        Computer-generated invoice from {b.portal_name || order.source_portal} · ShipOMS.in
      </div>
    </div>
  );
}

const STN_MAP = { "South Korea": "ICN", "Germany": "FRA", "United Kingdom": "LHR", "United States": "JFK", "United Arab Emirates": "DXB", "Japan": "NRT", "Singapore": "SIN", "Australia": "SYD", "Canada": "YYZ", "France": "CDG", "Italy": "FCO", "Netherlands": "AMS", "Spain": "MAD", "China": "PVG", "Malaysia": "KUL", "Thailand": "BKK" };

const Cell = ({ label, value, className = "" }) => (
  <div className={`px-2 py-1.5 ${className}`}>
    <div className="text-[8px] font-semibold uppercase tracking-wide text-slate-400 leading-tight">{label}</div>
    <div className="text-[11px] font-semibold text-slate-900 leading-snug break-words">{value || "-"}</div>
  </div>
);

const SectionHead = ({ children }) => (
  <div className="bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-700 border-y border-slate-300">{children}</div>
);

function LabelCard({ order, index, total }) {
  const b = order.branding || {};
  const s = order.sender || {};
  const r = order.receiver || {};
  const boxes = order.boxes || [];
  const box = boxes[index] || null;
  const baseAwb = order.tracking_number || order.awb_number;
  const company = b.company_name || order.source_portal || "ShipOMS";
  const destStn = STN_MAP[order.destination_country] || (order.destination_country || "").slice(0, 3).toUpperCase() || "-";
  const boxItemsStr = (bx) => (bx && bx.items && bx.items.length ? bx.items.map((it) => `${it.name} x${it.quantity}`).join(", ") : (bx ? bx.item_content : ""));
  const items = box ? (boxItemsStr(box) || order.package_type) : order.package_type;
  const weight = box ? box.weight : order.weight;
  const dims = box ? `${box.length}×${box.width}×${box.height} cm` : "-";
  return (
    <div className="print-area label-page bg-white text-slate-900 border border-slate-500 rounded-md overflow-hidden" data-testid={`awb-doc-box-${index}`}>
      <div className="flex items-start justify-between px-3 py-2 bg-slate-50 border-b border-slate-500">
        <div className="flex items-center gap-2">
          {b.logo_file_id
            ? <img src={`${API}/portal-logo/${b.logo_file_id}`} alt="" className="h-8 w-8 rounded object-contain bg-white p-0.5" />
            : <div className="h-8 w-8 rounded bg-primary/10 flex items-center justify-center"><Package className="h-4 w-4 text-primary" /></div>}
          <div>
            <div className="font-extrabold text-sm leading-none">{company}</div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">Export Shipping Label</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-bold text-sm">AWB: {baseAwb}</div>
          <div className="text-[10px] font-semibold text-slate-600">Box {index + 1} / {total}</div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-500 bg-white">
        <div className="shrink-0">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">AWB No.</div>
          <div className="text-2xl font-extrabold tracking-tight leading-tight">{baseAwb}</div>
          <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mt-0.5">Scan to Track</div>
        </div>
        <div className="flex justify-end overflow-hidden">
          <Barcode value={String(baseAwb)} height={82} width={2.8} fontSize={20} margin={0} />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="border-r border-slate-500">
          <SectionHead>From (Shipper)</SectionHead>
          <div className="grid grid-cols-2 divide-x divide-slate-300 border-b border-slate-300">
            <Cell label="Shipper's Ref" value={baseAwb} />
            <Cell label="Phone Number" value={s.phone || order.customer_phone} />
          </div>
          <Cell label="From" value={nameOf(s, order.user_name)} className="border-b border-slate-300" />
          <Cell label="Address" value={s.address || order.pickup_address} className="border-b border-slate-300 min-h-[40px]" />
          <div className="grid grid-cols-2 divide-x divide-slate-300 border-b border-slate-300">
            <Cell label="City" value={s.city} />
            <Cell label="State / Province" value={s.state} />
          </div>
          <div className="grid grid-cols-2 divide-x divide-slate-300">
            <Cell label="Country" value={s.country || "India"} />
            <Cell label="Zip Code" value={s.pincode} />
          </div>
          <SectionHead>To (Receiver)</SectionHead>
          <div className="grid grid-cols-2 divide-x divide-slate-300 border-b border-slate-300">
            <Cell label="AWB Number" value={baseAwb} />
            <Cell label="Phone Number" value={r.phone || order.recipient_phone} />
          </div>
          <Cell label="Company" value={nameOf(r, order.recipient_name)} className="border-b border-slate-300" />
          <Cell label="Address" value={r.address || order.delivery_address} className="border-b border-slate-300 min-h-[40px]" />
          <div className="grid grid-cols-2 divide-x divide-slate-300 border-b border-slate-300">
            <Cell label="City" value={r.city} />
            <Cell label="State / Province" value={r.state} />
          </div>
          <div className="grid grid-cols-2 divide-x divide-slate-300">
            <Cell label="Country" value={r.country} />
            <Cell label="Zip Code" value={r.pincode} />
          </div>
        </div>
        <div>
          <div className="grid grid-cols-2 divide-x divide-slate-300 border-b border-slate-300">
            <Cell label="Org. STN" value="DEL" />
            <Cell label="Dest. STN" value={destStn} />
          </div>
          <SectionHead>Shipment Information</SectionHead>
          <div className="grid grid-cols-2 divide-x divide-slate-300 border-b border-slate-300">
            <Cell label="No. of Pieces" value={`${index + 1}/${total}`} />
            <Cell label="Actual Weight" value={`${weight} kg`} />
          </div>
          <div className="grid grid-cols-2 divide-x divide-slate-300 border-b border-slate-300">
            <Cell label="Booking Date" value={fmtDate(order.created_at)} />
            <Cell label="Dimensions" value={dims} />
          </div>
          <div className="px-2 py-1.5" data-testid={`label-contents-${index}`}>
            <div className="flex items-center justify-between text-[8px] font-semibold uppercase tracking-wide text-slate-400 leading-tight">
              <span>Packing Item Content</span><span>Qty · Value</span>
            </div>
            {box && box.items && box.items.length ? (
              <div className="mt-0.5 divide-y divide-slate-100">
                {box.items.map((it, k) => (
                  <div key={k} className="flex items-center justify-between gap-3 py-0.5 text-[11px] font-semibold text-slate-900">
                    <span className="break-words">{it.name}</span>
                    <span className="shrink-0 tabular-nums text-slate-700">× {it.quantity} · {money(it.value)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-slate-300 pt-0.5 mt-0.5 text-[11px] font-extrabold text-slate-900">
                  <span>Total Value</span>
                  <span className="tabular-nums">{money(box.items.reduce((a, it) => a + (it.quantity || 1) * (it.value || 0), 0))}</span>
                </div>
              </div>
            ) : (
              <div className="text-[11px] font-semibold text-slate-900 leading-snug break-words">{items || "-"}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AwbLabel({ order }) {
  const boxes = order.boxes || [];
  const n = boxes.length || 1;
  const boxList = boxes.length ? boxes : [null];
  return (
    <div className="space-y-4" data-testid="awb-doc">
      {boxList.map((_, i) => <LabelCard key={i} order={order} index={i} total={n} />)}
    </div>
  );
}

export function PodDocument({ order }) {
  const b = order.branding || {};
  const company = b.company_name || order.source_portal || "ShipOMS";
  const awb = order.awb_number || order.tracking_number;
  const delivered = order.delivered_at || order.updated_at;
  return (
    <div className="print-area bg-white text-slate-900 p-6 rounded-lg" data-testid="pod-doc">
      <div className="flex items-start justify-between border-b-2 border-emerald-600 pb-4">
        <div className="flex items-center gap-2">
          {b.logo_file_id
            ? <img src={`${API}/portal-logo/${b.logo_file_id}`} alt="" className="h-11 w-11 rounded-lg object-contain border border-slate-200 bg-white" />
            : <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-600 text-white"><Package className="h-6 w-6" /></div>}
          <div>
            <div className="text-xl font-extrabold">{company}</div>
            <div className="text-xs text-slate-600 whitespace-pre-line">{b.company_address}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-extrabold uppercase tracking-wide text-emerald-600">Proof of Delivery</div>
          <div className="text-xs text-slate-600">AWB: <span className="font-semibold">{awb}</span></div>
          <div className="text-xs text-slate-600">Tracking: {order.tracking_number}</div>
        </div>
      </div>

      <div className="my-5 flex items-center justify-center">
        <div className="inline-flex items-center gap-2 rounded-lg border-2 border-emerald-600 px-5 py-2 text-emerald-700 font-extrabold uppercase tracking-widest text-lg -rotate-2">
          ✓ Delivered
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 text-sm">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Consignee</div>
          <div className="font-semibold">{order.recipient_name}</div>
          <div className="text-xs text-slate-600">{order.delivery_address}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Delivery Details</div>
          <div className="text-xs text-slate-700">Destination: <span className="font-semibold">{order.destination_country}</span></div>
          <div className="text-xs text-slate-700">Location: <span className="font-semibold">{order.delivered_location || order.destination_country}</span></div>
          <div className="text-xs text-slate-700">Delivered On: <span className="font-semibold">{fmtDate(delivered)}</span></div>
          {order.delivered_received_by && <div className="text-xs text-slate-700">Received By: <span className="font-semibold">{order.delivered_received_by}</span></div>}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-6 text-sm border-t border-slate-200 pt-4">
        <div className="text-xs text-slate-600">Pieces: {order.box_count || 1} · Weight: {order.weight} kg</div>
        <div className="text-right">
          <div className="mt-8 border-t border-slate-400 pt-1 text-[11px] text-slate-500 inline-block px-8">Authorised Signature</div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-slate-200 text-[10px] text-slate-400 text-center">
        Proof of Delivery issued by {b.portal_name || company} · ShipOMS.in
      </div>
    </div>
  );
}
